import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Plus, Trash2 } from "lucide-react";
import { usePricingData } from "@/hooks/usePricingData";
import { Skeleton } from "@/components/ui/skeleton";

export interface WebPortalUser {
  name: string;
  email: string;
  role: string;
}

export interface ValidConfig {
  plan: string;
  addons: string[];
  webPortal: boolean;
  webPortalUsers: WebPortalUser[];
}

interface Props {
  config: ValidConfig;
  onChange: (config: ValidConfig) => void;
}

const EMPTY_USER: WebPortalUser = { name: "", email: "", role: "" };
const MAX_PORTAL_USERS = 10;

export function OrderValidConfig({ config, onChange }: Props) {
  const { t } = useTranslation();
  const { plans, addons, loading } = usePricingData("valid");

  const handleAddonToggle = (addonName: string, checked: boolean) => {
    const updated = checked
      ? [...config.addons, addonName]
      : config.addons.filter((a) => a !== addonName);
    onChange({ ...config, addons: updated });
  };

  const handleWebPortalToggle = (checked: boolean) => {
    onChange({
      ...config,
      webPortal: checked,
      webPortalUsers: checked && config.webPortalUsers.length === 0
        ? [{ ...EMPTY_USER }]
        : config.webPortalUsers,
    });
  };

  const updatePortalUser = (index: number, field: keyof WebPortalUser, value: string) => {
    const updated = [...config.webPortalUsers];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...config, webPortalUsers: updated });
  };

  const addPortalUser = () => {
    if (config.webPortalUsers.length < MAX_PORTAL_USERS) {
      onChange({ ...config, webPortalUsers: [...config.webPortalUsers, { ...EMPTY_USER }] });
    }
  };

  const removePortalUser = (index: number) => {
    const updated = config.webPortalUsers.filter((_, i) => i !== index);
    onChange({
      ...config,
      webPortalUsers: updated,
      webPortal: updated.length > 0,
    });
  };

  // Find the selected plan's name to look up addon prices
  const selectedPlan = plans.find((p) => p.name.toLowerCase() === config.plan.toLowerCase());

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">
          {t("order.configureValid")}
        </h2>
        <p className="text-muted-foreground mt-1">
          {t("order.configureValidDesc")}
        </p>
      </div>

      {/* Plan selection */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t("order.choosePlan")}</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {plans.map((plan) => {
            const planKey = plan.name.toLowerCase();
            return (
              <button
                key={plan.id}
                onClick={() => onChange({ ...config, plan: planKey })}
                className={cn(
                  "flex flex-col items-center p-5 rounded-xl border-2 transition-all",
                  config.plan === planKey
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-border hover:border-primary/50"
                )}
              >
                <span className="text-sm font-bold text-foreground">
                  {plan.name}
                </span>
                <span className="text-lg font-semibold text-primary mt-1">
                  {plan.price}
                </span>
                <span className="text-xs text-muted-foreground">{t("order.perMonth")}</span>
                <span className="text-[10px] text-muted-foreground mt-1">{t("order.binding3")}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Addons */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">{t("order.addons")}</Label>
        <p className="text-xs text-muted-foreground">{t("order.addonsDesc")}</p>
        <div className="space-y-3">
          {addons.map((addon) => {
            const addonPrice = selectedPlan ? addon.prices[selectedPlan.name] : undefined;
            return (
              <label
                key={addon.id}
                className={cn(
                  "flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all",
                  config.addons.includes(addon.name)
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                )}
              >
                <div className="flex items-center gap-3">
                  <Checkbox
                    checked={config.addons.includes(addon.name)}
                    onCheckedChange={(checked) =>
                      handleAddonToggle(addon.name, checked === true)
                    }
                  />
                  <span className="text-sm font-medium">{addon.name}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {addonPrice || "–"}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Web portal access */}
      <div className="space-y-4">
        <label
          className={cn(
            "flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all",
            config.webPortal
              ? "border-primary bg-primary/5"
              : "border-border hover:border-primary/50"
          )}
        >
          <Checkbox
            checked={config.webPortal}
            onCheckedChange={(checked) => handleWebPortalToggle(checked === true)}
          />
          <div>
            <span className="text-sm font-medium">{t("order.webPortal.label")}</span>
            <p className="text-xs text-muted-foreground mt-0.5">{t("order.webPortal.desc")}</p>
          </div>
        </label>

        {config.webPortal && (
          <div className="space-y-4 pl-2">
            <p className="text-sm text-muted-foreground">{t("order.webPortal.usersDesc")}</p>

            {config.webPortalUsers.map((user, index) => (
              <div key={index} className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card">
                <div className="flex-1 grid sm:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t("order.userName")} *</Label>
                    <Input
                      value={user.name}
                      onChange={(e) => updatePortalUser(index, "name", e.target.value)}
                      placeholder={t("order.userNamePlaceholder")}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t("order.userEmail")} *</Label>
                    <Input
                      type="email"
                      value={user.email}
                      onChange={(e) => updatePortalUser(index, "email", e.target.value)}
                      placeholder={t("order.userEmailPlaceholder")}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t("order.userRole")}</Label>
                    <Input
                      value={user.role}
                      onChange={(e) => updatePortalUser(index, "role", e.target.value)}
                      placeholder={t("order.userRolePlaceholder")}
                    />
                  </div>
                </div>
                {config.webPortalUsers.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removePortalUser(index)}
                    className="shrink-0 mt-5 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}

            {config.webPortalUsers.length < MAX_PORTAL_USERS && (
              <Button variant="outline" size="sm" onClick={addPortalUser} className="gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                {t("order.webPortal.addUser")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
